import { IProspectList } from "../Interfaces/IProspectList";

export type SortValueType = "string" | "number" | "date";

const isBlank = (value: any): boolean =>
  value == null || value === "" || (typeof value === "number" && isNaN(value));

/**
 * Compare two cell values on the real value — numbers numerically, dates
 * chronologically, strings case-insensitively. Blank or unrecognised values
 * sort last in either direction (P.8), which is why blanks are handled outside
 * of the direction multiplier.
 */
export const compareCellValues = (
  a: any,
  b: any,
  type: SortValueType,
  direction: "asc" | "desc",
): number => {
  let aBlank = isBlank(a);
  let bBlank = isBlank(b);

  let aValue: any = a;
  let bValue: any = b;
  if (type === "date") {
    aValue = aBlank ? NaN : new Date(a).getTime();
    bValue = bBlank ? NaN : new Date(b).getTime();
    aBlank = aBlank || isNaN(aValue);
    bBlank = bBlank || isNaN(bValue);
  }

  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;

  let result = 0;
  if (type === "string") {
    result = `${aValue}`.localeCompare(`${bValue}`, undefined, {
      sensitivity: "base",
    });
  } else {
    result = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
  }
  return direction === "desc" ? -result : result;
};

/**
 * Sort rows by one field, blanks last in either direction. Stable for equal keys.
 */
export const sortRowsByField = <T>(
  rows: T[],
  field: keyof T,
  type: SortValueType,
  direction: "asc" | "desc",
): T[] =>
  [...rows].sort((a, b) =>
    compareCellValues(a[field], b[field], type, direction),
  );

/**
 * The Prospects grid's default order when no column sort is applied (P.3):
 * the signed-in user's clients first, then within each group onboarding
 * progress descending; company name breaks ties so the order is deterministic.
 */
export const defaultProspectOrder = (
  rows: IProspectList[],
  myEmail: string | null | undefined,
): IProspectList[] => {
  const mine = (row: IProspectList) => isMyProspect(row, myEmail);
  return [...rows].sort((a, b) => {
    const groupDiff = Number(mine(b)) - Number(mine(a));
    if (groupDiff !== 0) return groupDiff;
    const progressDiff = b.onboardingCompletedSteps - a.onboardingCompletedSteps;
    if (progressDiff !== 0) return progressDiff;
    return (a.companyName || "").localeCompare(b.companyName || "", undefined, {
      sensitivity: "base",
    });
  });
};

/**
 * The Only-my-clients match compares on the user identifier (email), never the
 * display name (P.3).
 */
export const isMyProspect = (
  row: IProspectList,
  myEmail: string | null | undefined,
): boolean =>
  !!myEmail &&
  !!row.tddaRelationshipManagerEmail &&
  row.tddaRelationshipManagerEmail.toLowerCase() === myEmail.toLowerCase();

const nzCurrency = new Intl.NumberFormat("en-NZ", {
  style: "currency",
  currency: "NZD",
});

/** Sales pipeline value as en-NZ currency, an em dash when empty (P.1/P.8). */
export const formatNzCurrency = (value: number | null | undefined): string =>
  value == null ? "—" : nzCurrency.format(value);

/** Empty-cell rendering default (P.8). */
export const emDashWhenEmpty = (value: any): string =>
  isBlank(value) ? "—" : `${value}`;

/** Row-count footer text, singular or plural (P.8). */
export const rowCountText = (count: number, noun: string = "prospect"): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;
